import { useState, useEffect, useRef } from 'react';
import { Building2, FileText, KeyRound, AlertCircle, CheckCircle2, XCircle, Plus, Trash2, Upload, Save, Plug } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import {
  useBillingSettings,
  useUpdateBillingSettings,
  useUploadCertificate,
  useUploadPrivateKey,
  usePointsOfSale,
  useCreatePointOfSale,
  useDeletePointOfSale,
  useUpdatePointOfSale,
  useTestConnection,
} from '@/hooks/useBilling';
import type { IvaCondition, ArcaEnvironment, BillingTestResult, PointOfSaleAppliesTo } from '@/services/billing';

export default function BillingSettings() {
  const { data: settings, isLoading } = useBillingSettings();
  const { data: pointsOfSale = [] } = usePointsOfSale();
  const updateSettings = useUpdateBillingSettings();
  const uploadCert = useUploadCertificate();
  const uploadKey = useUploadPrivateKey();
  const createPos = useCreatePointOfSale();
  const deletePos = useDeletePointOfSale();
  const updatePos = useUpdatePointOfSale();
  const testConnection = useTestConnection();

  const [form, setForm] = useState({
    cuit: '',
    razon_social: '',
    iva_condition: '' as IvaCondition | '',
    environment: 'homologacion' as ArcaEnvironment,
  });

  const [newPos, setNewPos] = useState<{ number: string; description: string; applies_to: PointOfSaleAppliesTo }>(
    { number: '', description: '', applies_to: 'all' },
  );
  const [testResult, setTestResult] = useState<BillingTestResult | null>(null);

  const certInputRef = useRef<HTMLInputElement>(null);
  const keyInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (settings) {
      setForm({
        cuit: settings.cuit ?? '',
        razon_social: settings.razon_social ?? '',
        iva_condition: settings.iva_condition ?? '',
        environment: settings.environment,
      });
    }
  }, [settings]);

  const handleSaveSettings = async () => {
    try {
      await updateSettings.mutateAsync({
        cuit: form.cuit || null,
        razon_social: form.razon_social || null,
        iva_condition: (form.iva_condition || null) as IvaCondition | null,
        environment: form.environment,
      });
      toast.success('Configuración guardada');
    } catch (e: any) {
      const msg = e?.response?.data?.detail ?? 'No se pudo guardar';
      toast.error(typeof msg === 'string' ? msg : 'Error al guardar');
    }
  };

  const handleCertUpload = async (file?: File | null) => {
    if (!file) return;
    try {
      await uploadCert.mutateAsync(file);
      toast.success('Certificado subido');
    } catch (e: any) {
      toast.error(e?.response?.data?.detail ?? 'No se pudo subir el certificado');
    }
  };

  const handleKeyUpload = async (file?: File | null) => {
    if (!file) return;
    try {
      await uploadKey.mutateAsync(file);
      toast.success('Clave privada subida');
    } catch (e: any) {
      toast.error(e?.response?.data?.detail ?? 'No se pudo subir la clave');
    }
  };

  const handleCreatePos = async () => {
    const number = Number(newPos.number);
    if (!number || !newPos.description.trim()) {
      toast.error('Completá número y descripción');
      return;
    }
    try {
      await createPos.mutateAsync({
        number,
        description: newPos.description.trim(),
        applies_to: newPos.applies_to,
      });
      setNewPos({ number: '', description: '', applies_to: 'all' });
      toast.success('Punto de venta creado');
    } catch (e: any) {
      toast.error(e?.response?.data?.detail ?? 'No se pudo crear el punto de venta');
    }
  };

  const handleTestConnection = async () => {
    setTestResult(null);
    try {
      const result = await testConnection.mutateAsync();
      setTestResult(result);
      if (result.ok) toast.success('Conexión con ARCA verificada');
    } catch (e: any) {
      const msg = e?.response?.data?.detail ?? 'No se pudo probar la conexión';
      toast.error(typeof msg === 'string' ? msg : 'Error al probar la conexión');
    }
  };

  const handleDeletePos = async (id: number) => {
    if (!confirm('¿Eliminar este punto de venta?')) return;
    try {
      await deletePos.mutateAsync(id);
      toast.success('Punto de venta eliminado');
    } catch (e: any) {
      // Un punto de venta con comprobantes autorizados no se puede borrar: el backend
      // lo rechaza y explica por que. Sin esto el error se perdia y no pasaba nada.
      const msg = e?.response?.data?.detail ?? 'No se pudo eliminar el punto de venta';
      toast.error(typeof msg === 'string' ? msg : 'No se pudo eliminar el punto de venta');
    }
  };

  const handleChangeAppliesTo = async (id: number, appliesTo: PointOfSaleAppliesTo) => {
    try {
      await updatePos.mutateAsync({ id, payload: { applies_to: appliesTo } });
      toast.success('Rubro actualizado');
    } catch (e: any) {
      toast.error(e?.response?.data?.detail ?? 'No se pudo cambiar el rubro');
    }
  };

  const handleTogglePos = async (id: number, isActive: boolean) => {
    try {
      await updatePos.mutateAsync({ id, payload: { is_active: !isActive } });
      toast.success(isActive ? 'Punto de venta desactivado' : 'Punto de venta activado');
    } catch (e: any) {
      toast.error(e?.response?.data?.detail ?? 'No se pudo cambiar el estado');
    }
  };

  if (isLoading) return <div className="p-6">Cargando…</div>;

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-taupe-800">Facturación electrónica</h1>
        <p className="text-taupe-500">Configuración para emitir comprobantes ante ARCA (AFIP)</p>
      </div>

      {/* Estado de configuración */}
      <div className={`rounded-xl border p-4 flex items-center gap-3 ${
        settings?.is_configured
          ? 'bg-green-50 border-green-200 text-green-800'
          : 'bg-amber-50 border-amber-200 text-amber-800'
      }`}>
        {settings?.is_configured ? <CheckCircle2 className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
        <div className="text-sm">
          {settings?.is_configured
            ? 'Listo para emitir comprobantes.'
            : 'Faltan datos para emitir comprobantes. Completá CUIT, razón social, condición IVA y subí los certificados.'}
        </div>
      </div>

      {/* Datos fiscales */}
      <div className="bg-white rounded-xl border border-cream-200 p-5 space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <Building2 className="h-5 w-5 text-taupe-600" />
          <h2 className="font-semibold text-taupe-800">Datos fiscales</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="CUIT"
            placeholder="20123456780"
            value={form.cuit}
            onChange={(e) => setForm({ ...form, cuit: e.target.value })}
            maxLength={11}
          />
          <Input
            label="Razón social"
            placeholder="Veterinaria San Martín S.A."
            value={form.razon_social}
            onChange={(e) => setForm({ ...form, razon_social: e.target.value })}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-taupe-700 mb-1">Condición IVA</label>
            <select
              value={form.iva_condition}
              onChange={(e) => setForm({ ...form, iva_condition: e.target.value as IvaCondition })}
              className="w-full px-3 py-2 border border-cream-300 rounded-lg focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none"
            >
              <option value="">Seleccionar…</option>
              <option value="monotributo">Monotributo</option>
              <option value="responsable_inscripto">Responsable Inscripto</option>
              <option value="exento">Exento</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-taupe-700 mb-1">Entorno ARCA</label>
            <select
              value={form.environment}
              onChange={(e) => setForm({ ...form, environment: e.target.value as ArcaEnvironment })}
              className="w-full px-3 py-2 border border-cream-300 rounded-lg focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none"
            >
              <option value="homologacion">Homologación (pruebas)</option>
              <option value="produccion">Producción (real)</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSaveSettings} loading={updateSettings.isPending}>
            <Save className="h-4 w-4 mr-2" />
            Guardar
          </Button>
        </div>
      </div>

      {/* Certificados */}
      <div className="bg-white rounded-xl border border-cream-200 p-5 space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <KeyRound className="h-5 w-5 text-taupe-600" />
          <h2 className="font-semibold text-taupe-800">Certificados ARCA</h2>
        </div>
        <p className="text-sm text-taupe-500">
          Subí el certificado (.crt) firmado por ARCA y la clave privada (.key) con la que se generó el CSR.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className={`rounded-lg border p-4 ${settings?.has_certificate ? 'border-green-200 bg-green-50' : 'border-cream-200'}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-taupe-800">Certificado (.crt)</span>
              {settings?.has_certificate && <CheckCircle2 className="h-4 w-4 text-green-600" />}
            </div>
            <input
              ref={certInputRef}
              type="file"
              accept=".crt,.pem,.cer"
              className="hidden"
              onChange={(e) => handleCertUpload(e.target.files?.[0])}
            />
            <Button
              variant="secondary"
              onClick={() => certInputRef.current?.click()}
              loading={uploadCert.isPending}
              className="w-full"
            >
              <Upload className="h-4 w-4 mr-2" />
              {settings?.has_certificate ? 'Reemplazar certificado' : 'Subir certificado'}
            </Button>
          </div>

          <div className={`rounded-lg border p-4 ${settings?.has_private_key ? 'border-green-200 bg-green-50' : 'border-cream-200'}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-taupe-800">Clave privada (.key)</span>
              {settings?.has_private_key && <CheckCircle2 className="h-4 w-4 text-green-600" />}
            </div>
            <input
              ref={keyInputRef}
              type="file"
              accept=".key,.pem"
              className="hidden"
              onChange={(e) => handleKeyUpload(e.target.files?.[0])}
            />
            <Button
              variant="secondary"
              onClick={() => keyInputRef.current?.click()}
              loading={uploadKey.isPending}
              className="w-full"
            >
              <Upload className="h-4 w-4 mr-2" />
              {settings?.has_private_key ? 'Reemplazar clave' : 'Subir clave'}
            </Button>
          </div>
        </div>
      </div>

      {/* Puntos de venta */}
      <div className="bg-white rounded-xl border border-cream-200 p-5 space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="h-5 w-5 text-taupe-600" />
          <h2 className="font-semibold text-taupe-800">Puntos de venta</h2>
        </div>
        <p className="text-sm text-taupe-500">
          Cargá los números de punto de venta declarados en ARCA. Al darlos de alta, el campo
          <strong> Sistema</strong> tiene que quedar en <strong>Web Services</strong>: si en la
          constancia dice "Factura en Línea" o "CAEA / Contingencias", ese punto de venta no
          sirve para facturar desde el sistema y ARCA rechaza los comprobantes.
        </p>
        <p className="text-sm text-taupe-500">
          Si declarás un punto de venta para <strong>Servicios</strong> y otro para
          <strong> Productos</strong>, una venta que tenga de los dos se factura partida: cada
          rubro por su punto de venta. Sirve para separar lo que está exento de Ingresos Brutos.
        </p>

        {/* Form crear */}
        <div className="flex flex-col md:flex-row gap-3 items-end">
          <div className="w-full md:w-32">
            <label className="block text-sm font-medium text-taupe-700 mb-1">Número</label>
            <input
              type="number"
              min={1}
              max={99999}
              value={newPos.number}
              onChange={(e) => setNewPos({ ...newPos, number: e.target.value })}
              placeholder="1"
              className="w-full px-3 py-2 border border-cream-300 rounded-lg focus:ring-2 focus:ring-taupe-400 outline-none"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-taupe-700 mb-1">Descripción</label>
            <input
              type="text"
              value={newPos.description}
              onChange={(e) => setNewPos({ ...newPos, description: e.target.value })}
              placeholder="Sucursal Centro"
              className="w-full px-3 py-2 border border-cream-300 rounded-lg focus:ring-2 focus:ring-taupe-400 outline-none"
            />
          </div>
          <div className="w-full md:w-44">
            <label className="block text-sm font-medium text-taupe-700 mb-1">Factura</label>
            <select
              value={newPos.applies_to}
              onChange={(e) =>
                setNewPos({ ...newPos, applies_to: e.target.value as PointOfSaleAppliesTo })
              }
              className="w-full px-3 py-2 border border-cream-300 rounded-lg focus:ring-2 focus:ring-taupe-400 outline-none"
            >
              <option value="all">Todo</option>
              <option value="services">Servicios</option>
              <option value="products">Productos</option>
            </select>
          </div>
          <Button onClick={handleCreatePos} loading={createPos.isPending}>
            <Plus className="h-4 w-4 mr-2" />
            Agregar
          </Button>
        </div>

        {/*
          Con dos o mas puntos de venta y ninguno marcado por rubro, la venta no se parte:
          al facturar hay que elegir uno a mano y sale todo junto ahi. Es justo lo que pasa
          si se cargaron antes de que existiera el rubro.
        */}
        {pointsOfSale.filter((p) => p.is_active).length > 1 &&
          !pointsOfSale.some((p) => p.applies_to === 'services') &&
          !pointsOfSale.some((p) => p.applies_to === 'products') && (
            <div className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                Tenés más de un punto de venta pero ninguno asignado a un rubro, así que las
                ventas no se separan solas: al facturar hay que elegir uno y sale todo junto.
                Si querés separar servicios de productos, marcá el rubro de cada uno en la
                columna <strong>Factura</strong>.
              </span>
            </div>
          )}

        {/* Listado */}
        {pointsOfSale.length === 0 ? (
          <div className="text-center py-6 text-taupe-400 text-sm">
            Todavía no agregaste puntos de venta.
          </div>
        ) : (
          <div className="border border-cream-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-cream-50 text-taupe-700">
                <tr>
                  <th className="px-3 py-2 text-left w-24">Número</th>
                  <th className="px-3 py-2 text-left">Descripción</th>
                  <th className="px-3 py-2 text-left w-44">Factura</th>
                  <th className="px-3 py-2 text-left w-24">Estado</th>
                  <th className="px-3 py-2 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {pointsOfSale.map((pos) => (
                  <tr key={pos.id} className="border-t border-cream-100">
                    <td className="px-3 py-2 font-mono font-medium text-taupe-800">
                      #{pos.number.toString().padStart(4, '0')}
                    </td>
                    <td className="px-3 py-2 text-taupe-700">{pos.description}</td>
                    <td className="px-3 py-2">
                      {/*
                        Editable: si el punto de venta se cargó antes de que existiera el
                        rubro, o quedó en "Todo" por defecto, la venta no se parte y hay
                        que poder corregirlo sin borrarlo y crearlo de nuevo.
                      */}
                      <select
                        value={pos.applies_to}
                        onChange={(e) =>
                          handleChangeAppliesTo(pos.id, e.target.value as PointOfSaleAppliesTo)
                        }
                        disabled={updatePos.isPending}
                        className="w-full px-2 py-1 border border-cream-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-taupe-400 outline-none disabled:opacity-60"
                      >
                        <option value="all">Todo</option>
                        <option value="services">Servicios</option>
                        <option value="products">Productos</option>
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => handleTogglePos(pos.id, pos.is_active)}
                        disabled={updatePos.isPending}
                        title={pos.is_active ? 'Desactivar' : 'Activar'}
                        className={`text-xs px-2 py-0.5 rounded-full transition hover:opacity-80 ${
                          pos.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {pos.is_active ? 'Activo' : 'Inactivo'}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => handleDeletePos(pos.id)}
                        className="text-red-500 hover:text-red-600 p-1"
                        title="Eliminar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Prueba de conexión */}
      <div className="bg-white rounded-xl border border-cream-200 p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Plug className="h-5 w-5 text-taupe-600" />
          <h2 className="font-semibold text-taupe-800">Probar conexión con ARCA</h2>
        </div>
        <p className="text-sm text-taupe-500">
          Verifica que ARCA responda, que el certificado autentique y que cada punto de venta
          esté habilitado para facturar. No emite ningún comprobante.
        </p>

        <Button
          variant="secondary"
          onClick={handleTestConnection}
          loading={testConnection.isPending}
        >
          <Plug className="h-4 w-4 mr-2" />
          Probar conexión
        </Button>

        {testResult ? (
          <div className="space-y-2">
            <div className={`text-sm font-medium ${testResult.ok ? 'text-green-700' : 'text-red-700'}`}>
              {testResult.ok
                ? `Todo listo para facturar (entorno: ${testResult.ambiente}).`
                : 'Hay algo que falta resolver:'}
            </div>
            <ul className="space-y-1">
              {testResult.checks.map((check) => (
                <li
                  key={check.step}
                  className={`flex items-start gap-2 text-sm rounded-lg px-3 py-2 ${
                    check.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                  }`}
                >
                  {check.ok ? (
                    <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  )}
                  <span>{check.detail}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}
